import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from './Sidebar';
import Header from './Header';
import { Toaster } from '../ui/sonner';

const pageTitles = {
  '/dashboard': 'Dashboard',
  '/patients': 'Patients',
  '/visits': 'Visits',
  '/services': 'Services',
  '/inventory': 'Inventory',
  '/billing': 'Billing',
  '/locations': 'Locations',
  '/users': 'User Management',
  '/employees': 'Employees',
  '/reports': 'Reports & Analytics',
  '/clinics': 'Clinics Management',
  '/jotform': 'JotForm',
  '/patient-intake-submissions': 'Patient Intake Submissions',
  '/settings': 'Clinic Settings'
};

const MainLayout = () => {
  const { user, loading, isDoctor, isSuperAdmin } = useAuth();
  const location = useLocation();
  
  // Sidebar collapsed state - persisted in localStorage
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar_collapsed');
    return saved === 'true';
  });

  // Save sidebar state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebar_collapsed', sidebarCollapsed.toString());
  }, [sidebarCollapsed]);

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => !prev);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Redirect super admin to clinics page instead of dashboard
  const isSuperAdminUser = isSuperAdmin();
  if (isSuperAdminUser && (location.pathname === '/dashboard' || location.pathname === '/')) {
    return <Navigate to="/clinics" replace />;
  }

  // Redirect doctors away from dashboard and patients pages
  const isDoctorUser = isDoctor();
  if (isDoctorUser && (location.pathname === '/dashboard' || location.pathname === '/patients')) {
    return <Navigate to="/visits" replace />;
  }

  const getTitle = () => {
    const basePath = '/' + location.pathname.split('/')[1];
    return pageTitles[basePath] || 'Clinic Management';
  };

  // For doctors: completely hide sidebar (no toggle)
  // For other roles: sidebar is always visible
  const showSidebar = !isDoctorUser;

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Sidebar - Only for non-doctors */}
      {showSidebar && (
        <Sidebar 
          collapsed={sidebarCollapsed} 
          onToggle={toggleSidebar}
          onNavigate={() => {}}
          showCloseButton={false}
        />
      )}
      
      <Header 
        collapsed={sidebarCollapsed} 
        title={getTitle()} 
        sidebarHidden={isDoctorUser}
      />
      <main className={`${isDoctorUser ? 'main-content-full' : 'main-content'} ${sidebarCollapsed && !isDoctorUser ? 'sidebar-collapsed' : ''}`}>
        <div className="p-6 md:p-8">
          <Outlet />
        </div>
      </main>
      <Toaster position="top-right" richColors />
    </div>
  );
};

export default MainLayout;
